import Phaser from 'phaser';
import { RunManager } from '../systems/RunManager';
import { SurvivorClass, CampManager } from '../systems/CampManager';
import { CLASS_DEFINITIONS } from '../constants';
import { SfxManager } from '../utils/SfxManager';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';

// === SCÈNE DE RENCONTRE SURVIVANT ===
// Le joueur rencontre un survivant et choisit : recruter, troquer, piller.

// Noms et dialogues aléatoires
const DIALOGUES = [
  '"Capitaine ! Je cherchais un équipage... Prenez-moi à bord !"',
  '"J\'ai survécu seul sur cette île maudite... Emmenez-moi."',
  '"Un navire ! Enfin ! Je sais me battre, laissez-moi prouver."',
  '"Les morts rôdent partout... J\'ai besoin de protection."',
  '"Je connais ces eaux. En échange d\'une place, je vous guide."',
  '"Pitié... Je n\'ai plus rien. Mais je ferai tout pour survivre."',
];

export class SurvivorScene extends Phaser.Scene {
  private run!: RunManager;
  private camp!: CampManager;
  private sfx = new SfxManager();
  private survivorClass!: SurvivorClass;
  private survivorName = '';
  private dialogue = '';
  private hasChosen = false;

  constructor() {
    super({ key: 'SurvivorScene' });
  }

  init(data: { run: RunManager }): void {
    this.run = data.run;
    this.camp = this.run.getCamp();
    this.hasChosen = false;

    // Générer un survivant aléatoire
    const classes: SurvivorClass[] = ['warrior', 'ranger', 'medic'];
    this.survivorClass = classes[Math.floor(Math.random() * classes.length)];
    this.survivorName = this.camp['generatePirateName']();
    this.dialogue = DIALOGUES[Math.floor(Math.random() * DIALOGUES.length)];
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e);

    const classDef = CLASS_DEFINITIONS[this.survivorClass];

    // Portrait du survivant
    this.add.circle(GAME_CX, 120, 40, classDef.color).setStrokeStyle(3, classDef.outline);
    this.add.text(GAME_CX, 120, classDef.emoji, {
      fontSize: '30px', fontFamily: FONT_BODY,
    }).setOrigin(0.5);

    // Nom et classe
    this.add.text(GAME_CX, 175, this.survivorName, {
      fontSize: '18px', color: '#ecf0f1', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5);

    this.add.text(GAME_CX, 200, `${classDef.emoji} ${classDef.name} — ${classDef.description}`, {
      fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5);

    // Stats
    const s = classDef.stats;
    this.add.text(GAME_CX, 225, `HP:${s.maxHp}  ATK:${s.attack}  DEF:${s.defense}  PA:${s.pa}  PM:${s.pm}`, {
      fontSize: '11px', color: '#3498db', fontFamily: FONT_BODY,
      wordWrap: { width: GAME_W - 20 }, align: 'center',
    }).setOrigin(0.5);

    this.add.text(GAME_CX, 245, `Passif : ${classDef.passive}`, {
      fontSize: '11px', color: '#f39c12', fontFamily: FONT_BODY,
      wordWrap: { width: GAME_W - 20 }, align: 'center',
    }).setOrigin(0.5);

    // Dialogue
    this.add.text(GAME_CX, 290, this.dialogue, {
      fontSize: '12px', color: '#ecf0f1', fontFamily: FONT_BODY, fontStyle: 'italic',
      wordWrap: { width: Math.min(500, GAME_W - 20) }, align: 'center',
    }).setOrigin(0.5);

    // === CHOIX ===
    const choiceY = 360;

    // Suffixe "(équipe)" pour les feedbacks HP quand > 1 membre vivant
    const aliveCount = this.run.getAliveTeam().length;
    const teamSuffix = aliveCount > 1 ? ' (équipe)' : '';

    // 1. Recruter (promesse — il rejoint le camp UNIQUEMENT si on survit ce run)
    // Bloqué si dortoir + recrutements en attente déjà au cap
    const aliveSurvivors = this.camp.getAliveSurvivors().length;
    const pending = this.run.getPendingRecruits().length;
    const survivorCap = this.camp.getSurvivorCapacity();
    const dormFull = (aliveSurvivors + pending) >= survivorCap;
    const recruitDesc = dormFull
      ? `Dortoir plein (${aliveSurvivors + pending}/${survivorCap}, recrutements en attente compris).`
      : 'Vous promet de rejoindre le camp à la fin du run (si vous survivez).';
    this.makeChoice(GAME_CX, choiceY, '🤝 Recruter', recruitDesc, dormFull ? '#7f8c8d' : '#2ecc71', () => {
      if (dormFull) return;
      // Recrutement différé : queue dans le RunManager, appliqué à la victoire (RunEndScene)
      this.run.queueRecruit(this.survivorClass, this.survivorName);
      this.showResult(`${this.survivorName} promet de rejoindre l'équipage si tu survis !`, '#2ecc71');
    }, !dormFull);

    // 2. Troquer (donne de l'or contre des soins pour toute l'équipe)
    const tradeGold = 15;
    const canTrade = this.run.getGold() >= tradeGold;
    this.makeChoice(GAME_CX, choiceY + 80, `💰 Troquer (${tradeGold} or)`, 'Échangez de l\'or contre des soins (+10 HP).', canTrade ? '#f39c12' : '#7f8c8d', () => {
      if (!canTrade) return;
      this.run.addGold(-tradeGold);
      this.run.applyHpDeltaToAliveTeam(10);
      this.showResult(`Vous troquez ${tradeGold} or contre des soins. +10 HP${teamSuffix} !`, '#f39c12');
    }, canTrade);

    // 3. Piller (voler de l'or, risqué — perte HP possible pour toute l'équipe)
    this.makeChoice(GAME_CX, choiceY + 160, '💀 Piller', 'Voler ses affaires. Risqué : toute l\'équipe pourrait être blessée.', '#e74c3c', () => {
      const lucky = Math.random() < 0.5;
      if (lucky) {
        const gold = 10 + Math.floor(Math.random() * 15);
        this.run.addGold(gold);
        this.showResult(`Vous dépouillez le survivant. +${gold} or.`, '#f39c12');
      } else {
        const dmg = 3 + Math.floor(Math.random() * 5);
        this.run.applyHpDeltaToAliveTeam(-dmg);
        const gold = 5;
        this.run.addGold(gold);
        this.showResult(`Il se défend ! -${dmg} HP${teamSuffix}, mais vous récupérez ${gold} or.`, '#e74c3c');
      }
    });

    // 4. Passer
    this.makeChoice(GAME_CX, choiceY + 240, '🚶 Passer son chemin', 'Ignorer le survivant.', '#7f8c8d', () => {
      this.showResult('Vous continuez votre route.', '#7f8c8d');
    });

    // Or et HP
    const hp = this.run.getPlayerHp();
    this.add.text(GAME_CX, 640, `❤️ ${hp > 0 ? hp : '?'} HP    💰 ${this.run.getGold()} Or`, {
      fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5);
  }

  private makeChoice(x: number, y: number, title: string, desc: string, color: string, onClick: () => void, enabled = true): void {
    const choiceW = Math.min(500, GAME_W - 20);
    const bg = this.add.rectangle(x, y + 15, choiceW, 60, 0x1a2540, enabled ? 1 : 0.4);
    bg.setStrokeStyle(1, 0x2a3a5c);

    const titleText = this.add.text(x, y + 5, title, {
      fontSize: '14px', color: enabled ? color : '#7f8c8d', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5);

    this.add.text(x, y + 25, desc, {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      wordWrap: { width: choiceW - 16 }, align: 'center',
    }).setOrigin(0.5);

    if (enabled) {
      bg.setInteractive({ useHandCursor: true });
      bg.on('pointerover', () => { bg.setStrokeStyle(2, 0xf1c40f); titleText.setColor('#f1c40f'); });
      bg.on('pointerout', () => { bg.setStrokeStyle(1, 0x2a3a5c); titleText.setColor(color); });
      bg.on('pointerdown', () => {
        if (this.hasChosen) return;
        this.hasChosen = true;
        this.sfx.select();
        onClick();
      });
    }
  }

  private showResult(message: string, color: string): void {
    const overlay = this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x000000, 0.7);
    const text = this.add.text(GAME_CX, 320, message, {
      fontSize: '16px', color, fontFamily: FONT_BODY, fontStyle: 'bold',
      wordWrap: { width: Math.min(500, GAME_W - 20) }, align: 'center',
    }).setOrigin(0.5).setAlpha(0);

    this.tweens.add({ targets: text, alpha: 1, duration: 400 });

    this.run.completeCurrentNode();

    this.time.delayedCall(1500, () => {
      startSceneWithFade(this, 'MapScene', { run: this.run });
    });
  }
}
